import {useEffect, useCallback, useState} from 'react';
import {UserInformation} from '@common/types/UserInformation';
import useFetch from '../../useFetch';
import usePrevious from '@vp-shared/utils/usePrevious';

type HookProps = {
  data: UserInformation | null;
  fetchData: () => void;
  isLoading: boolean;
  handleSave: (payload: object) => void;
  error: string | null;
  attempted: boolean;
  saveAttempted: boolean;
};
/**
 * Handle loading and saving of user info data
 *
 * @param {*} apiSpecs Array of objects {url, method, normalizer, onSuccess}
 * @param {*} fetchOpts Fetch options to useFetch hook
 */
const useUserInfoApi = (
  apiSpecs: {
    url: string;
    method: string;
    normalizer: (data: object) => UserInformation;
    onSuccess?: () => void;
  }[],
  fetchOpts: {
    sender: string;
  }
): HookProps => {
  const {data, isLoading, doRequest, attempted, error} = useFetch(fetchOpts);
  const wasLoading = usePrevious(isLoading);
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [inited, setInited] = useState(false);
  const onSuccess = apiSpecs[0].onSuccess;

  /**
   * Save user info
   */
  const handleSave = useCallback(
    (payload: object) => {
      setSaveAttempted(true);
      doRequest({
        url: apiSpecs[0].url,
        method: apiSpecs[0].method,
        body: {...apiSpecs[0].normalizer(payload)},
        json: false
      });
    },
    [doRequest, apiSpecs]
  );

  /**
   * Get user info
   */
  const url = apiSpecs[0].url;
  const fetchData = useCallback(() => {
    doRequest({url});
  }, [doRequest, url]);

  useEffect(() => {
    if (!inited) {
      fetchData();
      setInited(true);
    }
  }, [fetchData, inited]);

  useEffect(() => {
    if (saveAttempted && wasLoading && !isLoading && !error && onSuccess) {
      onSuccess();
    }
  }, [onSuccess, saveAttempted, isLoading, wasLoading, error]);

  return {
    data,
    fetchData,
    isLoading,
    handleSave,
    error,
    attempted,
    saveAttempted
  };
};

export default useUserInfoApi;
